import { observer } from 'mobx-react-lite';
import { View, Share, ScrollView } from 'react-native';
import { styleMobile, styleDesktop } from './PickerFamilyFriends.style';
import { Illustration } from '@photosi/rn-components/illustration/Illustration';
import { ILLUSTRATION_NAMES } from '@photosi/rn-components/illustration/illustration.helper';
import TextLato from '@photosi/rn-components/text/text-lato/TextLato';
import { useStore } from 'src/contexts/StoreContext';
import { Icon } from '@photosi/rn-components/icon/Icon';
import { FocusGroups } from '@photosi/rn-components/focus-provider/FocusProvider.constants';
import { ICON_NAMES } from '@photosi/rn-components/icon/icon.helper';
import { icon, neutrals } from 'src/theme/colors';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';
import Clipboard from '@react-native-clipboard/clipboard';
import { DynamicModalProps } from '../../DynamicModal.types';
import { useEffect, useState } from 'react';
import { EDITOR_BASE_URL } from 'src/app/settings';
import { ROUTE_PATHS } from 'src/routes/_shared/routes.constants';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import { SourcesEventType } from '../ModalUploadMultisource.constants';
import { TextIconButton } from '@photosi/rn-components/buttons/text-icon-button/TextIconButton';
import {
  TEXT_ICON_BUTTON_SIZES,
  TEXT_ICON_BUTTON_TYPES,
} from '@photosi/rn-components/buttons/text-icon-button/TextIconButton.constants';
import { LinearGradient } from 'expo-linear-gradient';
import { isShareApiSupported } from 'src/_shared/utils/is-share-api-supported/isShareApiSupported';
import { ShareButton } from '@photosi/rn-components/buttons/share-button/ShareButton';

export interface PickerFamilyFriendsProps extends DynamicModalProps {
  isOnline: boolean;
}

export const PickerFamilyFriends = observer((props: PickerFamilyFriendsProps) => {
  const {
    i18n,
    project: { createShareTokenFamilyAndFriends, shareTokenFamilyAndFriends },
  } = useStore();
  const { isDesktop } = useResponsiveScreen();
  const [url, setUrl] = useState<string>('');
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const style = isDesktop ? styleDesktop : styleMobile;

  useEffect(() => {
    if (props.isOnline) {
      if (!shareTokenFamilyAndFriends) {
        createShareTokenFamilyAndFriends().then(res => {
          generateUrl(res);
        });
      } else {
        generateUrl(shareTokenFamilyAndFriends);
      }
    }
  }, [props.isOnline]);

  const generateUrl = (token: string) => {
    const urlUploadFamilyAndFriends = `${EDITOR_BASE_URL}/${ROUTE_PATHS.UPLOAD_FAMILY_FRIENDS}?shareToken=${token}`;
    setUrl(urlUploadFamilyAndFriends);
  };
  const copyUrl = async () => {
    Clipboard.setString(`${url}&origin=copy_link`);

    setShowTooltip(true);
    setTimeout(() => {
      setShowTooltip(false);
    }, 2000);

    amplitude.trackEvent(AnalyticsEvents.dynamicPicker.shareLink, {
      type: 'copy_link',
      share_token_id: shareTokenFamilyAndFriends,
      source_type: SourcesEventType.FamilyFriends,
    });
  };

  return (
    <View style={style.wrapper}>
      <ScrollView
        style={{ width: '100%' }}
        contentContainerStyle={{ maxWidth: 500, alignSelf: 'center' }}
        horizontal={false}
        showsHorizontalScrollIndicator={false}
        showsVerticalScrollIndicator={false}
      >
        <View style={style.container}>
          <View style={style.illustration}>
            <Illustration name={ILLUSTRATION_NAMES.FamilyAndFriends} />
          </View>
          <View style={style.instructions}>
            <TextLato style={style.stepsTitle}>{i18n.t('modal-picker-family-friends:upload')}</TextLato>
            <View style={style.stepsWrapper}>
              {[1, 2, 3].map(step => {
                return (
                  <View
                    style={[style.stepContainer, step !== 1 && style.separator]}
                    key={`picker-family-friends-step-${step}`}
                  >
                    <View style={style.step}>
                      <TextLato style={style.stepNumber}>{step}</TextLato>
                      <View style={style.stepText}>
                        <TextLato style={style.stepTitle}>
                          {i18n.t(`modal-picker-family-friends:step-${step}-title`)}
                        </TextLato>
                        <TextLato style={style.stepDescription}>
                          {i18n.t(`modal-picker-family-friends:step-${step}-description`)}
                        </TextLato>
                      </View>
                    </View>
                    {step === 1 && (
                      <View style={style.share}>
                        <View style={style.linkWrapper}>
                          <TextLato style={style.ctaUrlShare} numberOfLines={1}>
                            {url}
                          </TextLato>
                          <View>
                            <LinearGradient
                              colors={['transparent', neutrals.white]}
                              style={style.gradient}
                              start={{ x: 0, y: 0 }}
                              end={{ x: 1, y: 0 }}
                              pointerEvents="none"
                            />
                            {showTooltip && (
                              <View style={style.tooltip}>
                                <View style={style.tooltipIcon}>
                                  <Icon name={ICON_NAMES.Check} filled color={icon.iconSuccess} />
                                </View>
                                <TextLato style={style.tooltipText}>
                                  {i18n.t('modal-picker-smartphone:copied')}
                                </TextLato>
                              </View>
                            )}
                            <TextIconButton
                              onPress={copyUrl}
                              focusId={'picker-family-friends-step-button-copy'}
                              focusOrder={0}
                              focusGroup={FocusGroups.LOOKUP_CHOICE}
                              text={i18n.t('modal-picker-family-friends:copy-cta')}
                              textStyle={style.buttonText}
                              style={{ minHeight: 32, minWidth: 118, justifyContent: 'center' }}
                              innerStyle={{
                                flexDirection: 'row',
                                justifyContent: 'center',
                                gap: 8,
                                alignItems: 'center',
                              }}
                              iconName={ICON_NAMES.Copy}
                              iconStyle={style.iconCopy}
                              size={TEXT_ICON_BUTTON_SIZES.SMALL}
                              type={TEXT_ICON_BUTTON_TYPES.ICON_LEFT}
                            />
                          </View>
                        </View>
                        {isShareApiSupported() && (
                          <TextLato style={style.textChoice}>
                            {i18n.t('modal-picker-family-friends:or-choice')}
                          </TextLato>
                        )}
                        <View>
                          <ShareButton
                            focusId={'picker-family-friends-step-button-share'}
                            focusGroup={FocusGroups.MODAL}
                            focusOrder={1}
                            style={{ minHeight: 'auto', borderWidth: 0, paddingHorizontal: 16, paddingVertical: 7 }}
                            cta={i18n.t('modal-picker-family-friends:share-cta')}
                            message={i18n.t('modal-picker-family-friends:share-message')}
                            url={url}
                            shareTokenFamilyAndFriends={shareTokenFamilyAndFriends}
                          />
                        </View>
                      </View>
                    )}
                  </View>
                );
              })}
            </View>
          </View>
        </View>
      </ScrollView>
    </View>
  );
});
